import type { DataTableColumn } from '@/components/ui/data-table-customized'
import type { DataTableFooterRow } from '@/components/ui/data-table-customized/types/footer'
import { formatNumeral } from '@/utils/function'
import dayjs from 'dayjs'
import type { ProductGroupMonthlyRevenue } from '../../../types'
import type { ColumnTotals } from './use-overview-data'

const formatPrice = (value: number) => {
    if (!value || value === 0) return '-'
    return formatNumeral(value, 0)
}

const formatPercent = (value: number) => {
    if (!value || value === 0) return '0.00%'
    return `${formatNumeral(value, 2)}%`
}

/** Cell style constants for each column category */
const CELL = {
    prevYear: 'text-right bg-purple-50/30',
    currYear: 'text-right text-gray-500',
    merged: 'text-right text-gray-500',
    unallocated: 'text-right bg-orange-50/30',
    allocType: 'text-right bg-green-50/50',
    unallocType: 'text-right bg-amber-50/50',
    summaryType: 'text-right font-medium bg-indigo-50/50 text-indigo-700',
    total: 'text-right font-medium bg-emerald-50/50 text-emerald-700',
    // Footer variants
    prevYearFt: 'text-right bg-purple-100/50 text-purple-800',
    currYearFt: 'text-right bg-blue-100/50 text-blue-800',
    unallocFt: 'text-right bg-orange-100/50 text-orange-800',
    allocTypeFt: 'text-right bg-green-100 text-green-800',
    unallocTypeFt: 'text-right bg-amber-100 text-amber-800',
    summaryTypeFt: 'text-right bg-indigo-100 text-indigo-800',
    totalFt: 'text-right bg-emerald-100 text-emerald-800',
} as const

type OverviewRow = ProductGroupMonthlyRevenue

/** Price render helper */
const priceRender = (value: any) => formatPrice(value as number)

/** Generate dynamic columns based on months and merge mode */
export function getOverviewColumns(
    months: string[],
    mergeAllocated: boolean,
    t: (key: string) => string
): DataTableColumn<OverviewRow>[] {
    const columns: DataTableColumn<OverviewRow>[] = [
        // Product group name (sticky)
        {
            key: 'product_group_name',
            title: t('overview.service'),
            width: 350,
            pinned: 'left',
            cellClassName: 'truncate',
        },
    ]

    // Monthly columns — dynamic per month
    months.forEach((month) => {
        const monthLabel = dayjs(month).format('MM/YYYY')

        if (mergeAllocated) {
            // Merged: single allocated column
            columns.push({
                key: `month_${month}_allocated` as any,
                title: `${monthLabel} - ${t('overview.pb_merged')}`,
                width: 150,
                cellClassName: CELL.merged,
                valueGetter: (row) => {
                    const md = row.monthly_data[month]
                    return (
                        (md?.allocated_previous_year || 0) +
                        (md?.allocated_current_year || 0)
                    )
                },
                render: priceRender,
            })
        } else {
            // Split: prev year + curr year
            columns.push(
                {
                    key: `month_${month}_prev` as any,
                    title: `${monthLabel} - ${t('overview.pb_prev_short')}`,
                    width: 150,
                    cellClassName: CELL.prevYear,
                    valueGetter: (row) =>
                        row.monthly_data[month]?.allocated_previous_year || 0,
                    render: priceRender,
                },
                {
                    key: `month_${month}_curr` as any,
                    title: `${monthLabel} - ${t('overview.pb_curr_short')}`,
                    width: 150,
                    cellClassName: CELL.currYear,
                    valueGetter: (row) =>
                        row.monthly_data[month]?.allocated_current_year || 0,
                    render: priceRender,
                }
            )
        }

        // KPB (unallocated) — always shown
        columns.push({
            key: `month_${month}_kpb` as any,
            title: `${monthLabel} - KPB`,
            width: 150,
            cellClassName: CELL.unallocated,
            valueGetter: (row) => row.monthly_data[month]?.unallocated || 0,
            render: priceRender,
        })
    })

    // Allocated by type
    const typeKeys: Array<{ field: keyof OverviewRow; labelKey: string }> = [
        { field: 'allocated_renewal', labelKey: 'payment_type.renewal' },
        { field: 'allocated_new_sale', labelKey: 'payment_type.new_sale' },
        { field: 'allocated_upsell', labelKey: 'payment_type.upsell' },
    ]
    typeKeys.forEach(({ field, labelKey }) => {
        columns.push({
            key: field as any,
            title: `${t('overview.allocated_by_type')} - ${t(labelKey)}`,
            width: 120,
            cellClassName: CELL.allocType,
            render: priceRender,
        })
    })

    // Unallocated by type
    const unallocTypeKeys: Array<{
        field: keyof OverviewRow
        labelKey: string
    }> = [
        { field: 'unallocated_renewal', labelKey: 'payment_type.renewal' },
        { field: 'unallocated_new_sale', labelKey: 'payment_type.new_sale' },
        { field: 'unallocated_upsell', labelKey: 'payment_type.upsell' },
    ]
    unallocTypeKeys.forEach(({ field, labelKey }) => {
        columns.push({
            key: field as any,
            title: `${t('overview.unallocated_by_type')} - ${t(labelKey)}`,
            width: 120,
            cellClassName: CELL.unallocType,
            render: priceRender,
        })
    })

    // Summary by type (allocated + unallocated)
    const summaryPairs: Array<{
        key: string
        allocField: keyof OverviewRow
        unallocField: keyof OverviewRow
        labelKey: string
    }> = [
        {
            key: 'summary_renewal',
            allocField: 'allocated_renewal',
            unallocField: 'unallocated_renewal',
            labelKey: 'payment_type.renewal',
        },
        {
            key: 'summary_new_sale',
            allocField: 'allocated_new_sale',
            unallocField: 'unallocated_new_sale',
            labelKey: 'payment_type.new_sale',
        },
        {
            key: 'summary_upsell',
            allocField: 'allocated_upsell',
            unallocField: 'unallocated_upsell',
            labelKey: 'payment_type.upsell',
        },
    ]
    summaryPairs.forEach(({ key, allocField, unallocField, labelKey }) => {
        columns.push({
            key: key as any,
            title: `${t('overview.total_by_type')} - ${t(labelKey)}`,
            width: 120,
            cellClassName: CELL.summaryType,
            valueGetter: (row) =>
                ((row[allocField] as number) || 0) +
                ((row[unallocField] as number) || 0),
            render: priceRender,
        })
    })

    // Total revenue
    columns.push({
        key: 'total_revenue',
        title: t('overview.total_revenue'),
        width: 150,
        cellClassName: CELL.total,
        render: priceRender,
    })

    // Percentage
    columns.push({
        key: 'percentage' as any,
        title: t('overview.percentage'),
        width: 80,
        cellClassName: CELL.total,
        valueGetter: (row) => row.total_revenue,
    })

    return columns
}

/** Build columns with percentage needing grandTotal (closure) */
export function buildOverviewColumns(
    months: string[],
    mergeAllocated: boolean,
    grandTotal: number,
    t: (key: string) => string
): DataTableColumn<OverviewRow>[] {
    const cols = getOverviewColumns(months, mergeAllocated, t)
    // Replace percentage column render with grandTotal closure
    const pctCol = cols.find((c) => c.key === 'percentage')
    if (pctCol) {
        pctCol.render = (_, row) => {
            const pct =
                grandTotal > 0 ? (row.total_revenue / grandTotal) * 100 : 0
            return formatPercent(pct)
        }
    }
    return cols
}

/** Generate footer row with totals */
export function getOverviewFooter(
    _columns: DataTableColumn<OverviewRow>[],
    months: string[],
    mergeAllocated: boolean,
    columnTotals: ColumnTotals,
    grandTotal: number,
    t: (key: string) => string
): DataTableFooterRow<OverviewRow> {
    const cells: DataTableFooterRow<OverviewRow>['cells'] = {}

    // Product group name cell → "Tổng"
    cells['product_group_name'] = t('overview.total')

    // Monthly footer cells
    months.forEach((month) => {
        const mt = columnTotals.monthly[month]
        if (mergeAllocated) {
            cells[`month_${month}_allocated`] = formatPrice(
                (mt?.allocated_previous_year || 0) +
                    (mt?.allocated_current_year || 0)
            )
        } else {
            cells[`month_${month}_prev`] = formatPrice(
                mt?.allocated_previous_year || 0
            )
            cells[`month_${month}_curr`] = formatPrice(
                mt?.allocated_current_year || 0
            )
        }
        cells[`month_${month}_kpb`] = formatPrice(mt?.unallocated || 0)
    })

    // Allocated by type
    cells['allocated_renewal'] = formatPrice(columnTotals.allocatedRenewal)
    cells['allocated_new_sale'] = formatPrice(columnTotals.allocatedNewSale)
    cells['allocated_upsell'] = formatPrice(columnTotals.allocatedUpsell)

    // Unallocated by type
    cells['unallocated_renewal'] = formatPrice(columnTotals.unallocatedRenewal)
    cells['unallocated_new_sale'] = formatPrice(columnTotals.unallocatedNewSale)
    cells['unallocated_upsell'] = formatPrice(columnTotals.unallocatedUpsell)

    // Summary by type
    cells['summary_renewal'] = formatPrice(
        columnTotals.allocatedRenewal + columnTotals.unallocatedRenewal
    )
    cells['summary_new_sale'] = formatPrice(
        columnTotals.allocatedNewSale + columnTotals.unallocatedNewSale
    )
    cells['summary_upsell'] = formatPrice(
        columnTotals.allocatedUpsell + columnTotals.unallocatedUpsell
    )

    // Grand total + percentage
    cells['total_revenue'] = formatPrice(grandTotal)
    cells['percentage'] = '100.00%'

    return {
        cells,
        cellClassName: 'font-bold',
    }
}
